import React, { useState, useMemo, useEffect, useCallback } from "react"
import { Platform, Keyboard, Dimensions, ListRenderItemInfo, Modal, Image as RNImage } from "~/ui/primitives"
import { ButtonText } from "~components/button"
import { CameraRoll } from "~/ui/camera-roll"
import * as MediaLibrary from "~/ui/expo-media-library"
import { Video as ExpoVideo } from "~/ui/expo-av"
import FastImage from "~/ui/FastImage"
import { Photo as PhotoType, PhotosModelProps } from "./photos-mode"
import styled from '~/ui/styled'
// import { debug } from "~utils"
import PhotosSelection from "./photos-selection"
import { alertWarning } from "~utils/alert"
import { MaskLoading } from "~components/mask-loading"
import { useTheme } from '~/ui/styled'
import useMediaLibrary from "~hooks/useMediaLibrary"
import { getAssetInfoFromAssetUri } from "~utils/getAssetDetail"

const imageWidth = Dimensions.get("window").width / 4

const Wrap = styled.View`
  flex: 1;
  height: 350px;
  align-items: center;
  justify-content: center;
`
const List = styled.FlatList`
  flex: 1;
  flex-wrap: wrap;
`
const ImageWrap = styled.Pressable`
  align-self: flex-start;
  align-items: center;
  width: ${imageWidth}px;
  padding: 5px;
`

const PhotoItem = ({ onClick, children }) => {
  return <ImageWrap onClick={onClick}>{children}</ImageWrap>
}

const Photo = styled(FastImage)`
  width: 100%;
  height: 100px;
`

const VideoWrap = styled.View`
  width: 100%;
  height: 100px;
  position: relative;
`

const Video = styled(ExpoVideo)`
  width: 100%;
  height: 100px;
`

// iOS 26+ 下 AVPlayer 对相册原始路径的访问有安全沙盒限制，
// 用 RN Image + ph:// 走 PhotoKit 拿首帧缩略图更稳定。
const VideoPoster = styled(RNImage)`
  width: 100%;
  height: 100px;
`

const CheckBoxWrap = styled.Pressable`
  position: absolute;
  top: 5px;
  right: 5px;
  padding: 4px;
  z-index: 1;
`

const CheckSolid = styled.View`
  width: 20px;
  height: 20px;
  border-radius: 10px;
  background-color: ${({ theme }) => theme.colors.primary};
  border-style: solid;
  border-color: white;
  border-width: 2px;
  justify-content: center;
  align-items: center;
`

const CheckOutline = styled.View`
  width: 20px;
  height: 20px;
  border-radius: 10px;
  background-color: ${({ theme }) => theme.colors.text};
  opacity: 0.6;
  border-style: solid;
  border-color: white;
  border-width: 1.5px;
  justify-content: center;
  align-items: center;
`

const CheckSolidText = styled.Text`
  color: ${({ theme }) => theme.colors.blockBackground};
  font-size: ${({ theme }) => theme.fontSizes.xs};
`

const DurationWrap = styled.View`
  position: absolute;
  right: 6px;
  bottom: 6px;
`

const DurationText = styled.Text`
  color: white;
  font-size: ${({ theme }) => theme.fontSizes.xs};
`

const PhotosSelectionToolsBar = styled.View`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  padding: ${({ theme }) => `${theme.space.sm} ${theme.space.xs}`};
`

const Photos: React.FC<PhotosModelProps> = ({
  selectedPhoto,
  setSelectedPhoto,
  selectedPhotos,
  setSelectedPhotos,
  submitSelectedPhoto,
  submitSelectedPhotos,
}) => {
  useEffect(() => {
    if (Platform.OS !== "ios") return
    // RN 类型要求完整 KeyboardEvent；原生实现仅使用 duration / easing
    Keyboard.scheduleLayoutAnimation({
      duration: 500,
      easing: "keyboard",
      endCoordinates: { screenX: 0, screenY: 0, width: 0, height: 0 },
    })
  }, [])

  const theme = useTheme()
  const [visible, setVisible] = useState(null)
  const [photos, setPhotos] = useState([])
  const [pageInfo, setPageInfo] = useState(undefined)
  const [photosSelectionVisible, setPhotosSelectionVisible] = useState(false)
  const [curSelectedIndex, setCurSelectedIndex] = useState(undefined)
  const [isSearchLoading, setIsSearchLoading] = useState(false)

  const { permission, requestPermissionsAsyncWithCallback } = useMediaLibrary({ writeOnly: false })
  const accessPrivileges = useMemo(() => {
    return permission?.accessPrivileges
  }, [permission])

  const getUserPhotos = useCallback(
  (endCursor, prevPhotos, isChange) => {
    setIsSearchLoading(true)

    // 参数略有不同：first 对应 batchSize，after 对应 end_cursor
    return CameraRoll.getPhotos({
      first: 16,
      after: isChange ? undefined : endCursor,
      assetType: 'All', // 包含图片和视频
      include: ['filename', 'fileSize', 'imageSize', 'playableDuration'] // 关键：直接带出大小，省去
    })
    .then(async(r) => {
      const { edges = [], page_info } = r
      // 转换数据格式以适配你现有的 renderItem
      const newPics = await Promise.all(
        edges.map(async (edge) => {
          if (Platform.OS === "ios") {
            const fileData = await CameraRoll.iosGetImageDataById(edge.node.image.uri);
            const ext = edge.node.image.filename.split(".")[1].toLowerCase();
            const isVideo = edge.node.type.startsWith('video');
            return {
              id: fileData.node.image.filepath,
              uri: fileData.node.image.filepath,
              previewUri: edge.node.image.uri,
              mediaType: isVideo ? 'video' : 'photo',
              filename: `${fileData.node.image.filename.split(".")[0]}.${ext}`,
              size: fileData.node.image.fileSize,
              duration: fileData.node.image.playableDuration,
              type: isVideo ? "video/mp4" : "image/jpeg",
              content_type: isVideo ? "video/mp4" : "image/jpeg"
            };
          } else {
            const fileData = await getAssetInfoFromAssetUri(edge.node.image.uri);
            const ext = edge.node.image.filename.split(".")[1].toLowerCase();
            const isVideo = edge.node.type.startsWith('video');
            return {
              id: fileData.uri,
              uri: fileData.uri,
              previewUri: fileData.uri,
              mediaType: isVideo ? 'video' : 'photo',
              filename: `${edge.node.image.filename.split(".")[0]}.${ext}`,
              size: edge.node.image.fileSize,
              duration: edge.node.image.playableDuration,
              type: isVideo ? "video/mp4" : "image/jpeg",
              content_type: isVideo ? "video/mp4" : "image/jpeg"
            }
          }
          
        })
      );
      // console.log('newPics', newPics)
      setPhotos((prev) => {
        const allPhotos = isChange ? newPics : [...prev, ...newPics];
        // 根据 id 去重
        const uniquePhotos = Array.from(new Map(allPhotos.map(item => [item.id, item])).values());
        return uniquePhotos;
      });
      setPageInfo(page_info)
    })
    .catch((err) => {})
    .finally(() => setIsSearchLoading(false))
  },
  [pageInfo, photos],
)
  // 选中图片进行预览
  const onSelectImage = useCallback(async (photo, index) => {
    setSelectedPhoto(await photo)
    setCurSelectedIndex(index)
    // setPhotosSelectionVisible(true)
  }, [])

  const handleSelectMultiple = useCallback(
    async (photo) => {
      const findedIndex = selectedPhotos.findIndex(({ id }) => id === photo.id)
      if (findedIndex > -1) {
        const copiedSelectedPhotos = selectedPhotos.slice()
        copiedSelectedPhotos.splice(findedIndex, 1)
        setSelectedPhotos(copiedSelectedPhotos)
      } else {
        const newSelectedPhotos = [...selectedPhotos, await photo]
        if (newSelectedPhotos.length > 9) {
          alertWarning({ message: "最多选择9张图片!" })
          return
        }
        setSelectedPhotos(newSelectedPhotos)
      }
    },
    [selectedPhotos],
  )

  const renderItem = useCallback(
    ({ photo, checked, order, index }) => {
      return (
        <PhotoItem onClick={() => onSelectImage(photo, index)}>
          <CheckBoxWrap onClick={() => handleSelectMultiple(photo)}>
            {checked ? (
              <CheckSolid>
                <CheckSolidText>{order}</CheckSolidText>
              </CheckSolid>
            ) : (
              <CheckOutline></CheckOutline>
            )}
          </CheckBoxWrap>
          {photo.mediaType === "photo" && <Photo src={photo.uri} fallback={true} />}
          {photo.mediaType === "video" && (
            <VideoWrap>
              {Platform.OS === "ios" ? (
                <VideoPoster
                  src={photo.previewUri || photo.uri}
                  resizeMode="cover"
                />
              ) : (
                <Video
                  src={photo.uri}
                  resizeMode="cover"
                />
              )}
              {photo.duration && (
                <DurationWrap>
                  <DurationText>{Math.round(photo.duration)}s</DurationText>
                </DurationWrap>
              )}
            </VideoWrap>
          )}
        </PhotoItem>
      )
    },
    [onSelectImage, handleSelectMultiple],
  )
  const renderItemFunc = useCallback(
    ({ item: photo, index }: ListRenderItemInfo<PhotoType>) => {
      const fIndex = selectedPhotos.findIndex((sPhoto: PhotoType) => sPhoto.id === photo.id)
      const checked = fIndex > -1
      const order = fIndex + 1
      return renderItem({ photo, checked, order, index })
    },
    [renderItem],
  )

  const openPhotosSheet = useCallback(() => {
    setVisible(true)
    getUserPhotos(pageInfo?.end_cursor, photos, false)
  }, [pageInfo, photos])

  const handleEndReached = useCallback(() => {
    if (isSearchLoading) return
    if (pageInfo && pageInfo.has_next_page === false) return
    getUserPhotos(pageInfo?.end_cursor, photos, false)
  }, [pageInfo, photos, isSearchLoading, getUserPhotos])

  useEffect(() => {
    const subscription = MediaLibrary.addListener(() => {
      getUserPhotos(pageInfo?.end_cursor, photos, true)
    })
    return () => {
      MediaLibrary.removeSubscription(subscription)
    }
  }, [pageInfo, photos])

  useEffect(() => {
    if (!visible) {
      requestPermissionsAsyncWithCallback({
        callback: openPhotosSheet,
      })
    }
  }, [visible, requestPermissionsAsyncWithCallback, openPhotosSheet])
  if (!visible) return null

  return (
    <Wrap>
      {accessPrivileges === "limited" && (
        <PhotosSelectionToolsBar>
          <ButtonText
            onClick={() => {
              MediaLibrary.presentPermissionsPickerAsync()
            }}
            text={"更多"}
            color={theme.colors.primary}
          />
        </PhotosSelectionToolsBar>
      )}
      <List
        onEndReachedThreshold={0.5}
        onEndReached={handleEndReached}
        bounces={false}
        extraData={photos.length}
        numColumns={4}
        data={photos}
        renderItem={renderItemFunc}
        keyExtractor={(item: PhotoType) => item.id}
      />
      {isSearchLoading && (
        <MaskLoading
          backgroundColor="rgba(255,255,255,0.2)"
          color={theme.colors.primary}
        ></MaskLoading>
      )}
      <Modal animationType="slide" transparent={true} visible={photosSelectionVisible}>
        <PhotosSelection
          setVisible={setPhotosSelectionVisible}
          curSelectedIndex={curSelectedIndex}
          setCurSelectedIndex={setCurSelectedIndex}
          handleSelectMultiple={handleSelectMultiple}
          photos={photos}
          selectedPhoto={selectedPhoto}
          setSelectedPhoto={setSelectedPhoto}
          selectedPhotos={selectedPhotos}
          setSelectedPhotos={setSelectedPhotos}
          submitSelectedPhoto={submitSelectedPhoto}
          submitSelectedPhotos={submitSelectedPhotos}
        ></PhotosSelection>
      </Modal>
    </Wrap>
  )
}

export default Photos
